import { User } from "@fairfleet/geotab";
import { useMemo } from "react";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useUsersByUserIds } from "@/hooks/users/useUsersByUserIds";
import { isRealDriverId } from "@/utilities/user/isRealDriverId";

/** The driver on a vehicle changes rarely; a 15 s poll would rebuild every row for nothing. */
const ASSIGNMENT_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/** Which driver is currently on which vehicle, and who those drivers are. */
export function useDriverAssignments(deviceIds: string[]) {
  const {
    data: statusInfoByDeviceId = {},
    isLoading: isLoadingStatus,
    isError: isErrorStatus,
  } = useStatusInfosByIds(deviceIds, [], ASSIGNMENT_REFETCH_INTERVAL_MS);

  const driverIdByDeviceId = useMemo(() => {
    const result: Record<string, string | undefined> = {};

    for (const deviceId of deviceIds) {
      result[deviceId] = statusInfoByDeviceId[deviceId]?.driver?.id;
    }

    return result;
  }, [statusInfoByDeviceId, deviceIds]);

  const deviceIdByDriverId = useMemo(() => {
    const result = new Map<string, string>();

    for (const [deviceId, driverId] of Object.entries(driverIdByDeviceId)) {
      if (driverId) result.set(driverId, deviceId);
    }

    return result;
  }, [driverIdByDeviceId]);

  // By id, not from the group-scoped driver list: the driver on a vehicle of the picked depot
  // can belong to company groups outside it and would otherwise show as unassigned.
  const assignedDriverIds = useMemo(
    () => (isLoadingStatus ? undefined : [...deviceIdByDriverId.keys()].filter(isRealDriverId)),
    [isLoadingStatus, deviceIdByDriverId],
  );
  const {
    data: driversData,
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
  } = useUsersByUserIds(assignedDriverIds);

  const driverById = useMemo(
    () => new Map<string, User>((driversData ?? []).map(user => [user.id, user])),
    [driversData],
  );

  return {
    driverIdByDeviceId,
    deviceIdByDriverId,
    driverById,
    isLoading: isLoadingStatus || isLoadingUsers,
    isError: isErrorStatus || isErrorUsers,
  };
}
